import { z } from 'zod';

import {
  DG_DUPLICATE_OPTION_MESSAGE,
  DG_QUESTION_TYPES,
  type DgContent,
  type DgOption,
  type DgQuestion,
  type DgSection,
  type DgStimulus,
  GENERATED_DG_QUESTION_TYPES,
  dgOptionWording,
} from './artifacts.ts';
import { type ArtifactChangeNotes, reconciledVersionText } from './change-notes.ts';
import { DomainError } from './errors.ts';
import { guideQuestionsInOrder } from './guide-coverage.ts';
import type { ArtifactVersionReason } from './version-reasons.ts';

/**
 * Placeholder policy bounds on guide wording, well inside the storage bounds the content schema
 * holds: about a sentence or two of question, a short phrase per option, a handful of options, and
 * a short section title. Product owns all of them.
 */
export const MAX_DG_QUESTION_CHARS = 300;
export const MAX_DG_OPTION_CHARS = 200;
export const MIN_DG_OPTIONS = 2;
export const MAX_DG_OPTIONS_DRAFTED = 8;
export const MAX_DG_SECTION_TITLE_CHARS = 120;

/** The longest note a reshape carries: what the researcher asked for, in a sentence or two. */
export const MAX_DG_NOTE_CHARS = 500;

/** The most changes one reshape carries: far above any one request, and a bound on a runaway. */
export const MAX_DG_RESHAPE_OPS = 50;

const words = (max: number) => z.string().trim().min(1).max(max);

const questionWordsSchema = words(MAX_DG_QUESTION_CHARS);
const optionWordsSchema = words(MAX_DG_OPTION_CHARS);

const flattenQuestions = (content: DgContent): DgQuestion[] =>
  content.sections.flatMap((section) => section.questions);

/**
 * Each question's number by id: 1-based and counted across the whole guide in the order
 * participants meet the questions, not restarted per section. The one numbering every surface that
 * names a guide question by number uses, so "question 6" in the coverage check's reason is the
 * same question wherever else it is numbered. Its keys iterate in number order
 * (`guideQuestionsInOrder`), so a reader mapping a number back to a question reads the same list.
 */
export const dgQuestionNumbers = (content: DgContent): ReadonlyMap<string, number> =>
  new Map(guideQuestionsInOrder(content).map((question, index) => [question.id, index + 1]));

const questionNotFound = (questionId: string): DomainError =>
  new DomainError('dg_question_not_found', 'That question is not in this guide.', {
    question_id: questionId,
  });

const sectionNotFound = (sectionId: string): DomainError =>
  new DomainError('dg_section_not_found', 'That section is not in this guide.', {
    section_id: sectionId,
  });

const invalidGuide = (message: string, detail: Record<string, unknown> = {}): DomainError =>
  new DomainError('invalid_content', message, { kind: 'dg', ...detail });

/**
 * Refuses a question type no agent-authored guide may hold yet: every question must be one of
 * `GENERATED_DG_QUESTION_TYPES`. Asked of every agent write of a guide, so a several-answer poll is
 * refused on the server rather than only discouraged by a prompt.
 */
export const assertGeneratedDgKinds = (content: DgContent): void => {
  const allowed = new Set<string>(GENERATED_DG_QUESTION_TYPES);
  const refused = flattenQuestions(content).filter((question) => !allowed.has(question.type));
  if (refused.length === 0) {
    return;
  }

  throw new DomainError(
    'dg_question_kind_unavailable',
    'A poll where people choose several answers is not available yet; use a poll with one answer, or an open question.',
    {
      question_ids: refused.map((question) => question.id),
      allowed: GENERATED_DG_QUESTION_TYPES,
    },
  );
};

/**
 * Refuses a guide with two options of one question worded alike (`dgOptionWording`), with the
 * sentence the researcher is shown. Asked of every guide write and approval, never of a read, so a
 * guide stored before the rule still loads.
 */
export const assertDgOptionsWordedApart = (content: DgContent): void => {
  const alike = flattenQuestions(content).filter((question) => {
    const wordings = (question.options ?? []).map((option) => dgOptionWording(option.text));
    return new Set(wordings).size !== wordings.length;
  });
  if (alike.length > 0) {
    throw invalidGuide(DG_DUPLICATE_OPTION_MESSAGE, {
      question_ids: alike.map((question) => question.id),
    });
  }
};

/** The research questions no question in the guide serves, in the order given. */
export const unservedRqIds = (content: DgContent, rqIds: readonly string[]): string[] => {
  const served = new Set(flattenQuestions(content).flatMap((question) => question.serves_rq_ids));

  return rqIds.filter((id) => !served.has(id));
};

/**
 * Refuses a guide that leaves a current research question with nothing serving it: the structural
 * floor under shortening a guide. A research question the researcher no longer wants is removed on
 * the Questions step, which is the one place that removal is theirs to make.
 */
export const assertEveryRqServed = (content: DgContent, rqIds: readonly string[]): void => {
  const unserved = unservedRqIds(content, rqIds);
  if (unserved.length === 0) {
    return;
  }

  throw new DomainError(
    'dg_rq_unserved',
    'That would leave a research question with no question in the guide asking about it. Keep at least one question for it, or remove the research question on the Questions step.',
    { unserved_rq_ids: unserved },
  );
};

/** The ids of `items` that `others` also holds, in `items`' order. */
const keptIds = <T extends { id: string }>(items: readonly T[], others: readonly T[]): string[] => {
  const ids = new Set(others.map((item) => item.id));

  return items.filter((item) => ids.has(item.id)).map((item) => item.id);
};

/** A question's answer wording, in order; none for an open question. */
const answerWording = (options: readonly { text: string }[] | undefined): string[] =>
  (options ?? []).map((option) => option.text);

const sameList = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((id, index) => id === right[index]);

/** Which section each question sits in. */
const sectionOfQuestion = (content: DgContent): Map<string, string> =>
  new Map(
    content.sections.flatMap((section) =>
      section.questions.map((question) => [question.id, section.id] as const),
    ),
  );

/** Whether the questions, or the sections holding them, come in a different order. */
export const dgOrderChanged = (previous: DgContent, current: DgContent): boolean => {
  const questionsBefore = flattenQuestions(previous);
  const questionsAfter = flattenQuestions(current);
  const sectionsBefore = sectionOfQuestion(previous);
  const sectionsAfter = sectionOfQuestion(current);

  return (
    !sameList(keptIds(questionsBefore, questionsAfter), keptIds(questionsAfter, questionsBefore)) ||
    !sameList(
      keptIds(previous.sections, current.sections),
      keptIds(current.sections, previous.sections),
    ) ||
    questionsAfter.some(
      (question) =>
        sectionsBefore.has(question.id) &&
        sectionsBefore.get(question.id) !== sectionsAfter.get(question.id),
    )
  );
};

const countLine = (count: number, one: string, many: string, rest: string): string =>
  count === 1 ? `One ${one} ${rest}` : `${String(count)} ${many} ${rest}`;

const sameStimulus = (left: DgStimulus | undefined, right: DgStimulus | undefined): boolean =>
  left === right ||
  (left !== undefined &&
    right !== undefined &&
    left.materials_artifact_id === right.materials_artifact_id &&
    left.materials_version === right.materials_version &&
    left.file_id === right.file_id);

/**
 * How many questions show an image they did not show before — a new image, or another one in place
 * of the last — and how many questions still in the guide stopped showing one.
 */
const stimulusChanges = (
  previous: DgContent | null,
  current: DgContent,
): { shown: number; removed: number } => {
  const before = new Map(
    (previous === null ? [] : flattenQuestions(previous)).map((q) => [q.id, q.stimulus] as const),
  );
  const questions = flattenQuestions(current);

  return {
    shown: questions.filter(
      (question) =>
        question.stimulus !== undefined &&
        before.get(question.id)?.file_id !== question.stimulus.file_id,
    ).length,
    removed: questions.filter(
      (question) => question.stimulus === undefined && before.get(question.id) !== undefined,
    ).length,
  };
};

/**
 * The line a guide version's reason reads as, on the version line and in the history.
 *
 * Counts come from the version itself compared with the one before it, so the line says what that
 * version actually did: an addition of one poll reads "One poll added at your request". Placeholder
 * copy; a version with no reason reads as nothing.
 */
export const dgVersionReasonText = ({
  reason,
  previous,
  current,
  notes = null,
}: {
  reason: ArtifactVersionReason | null;
  previous: DgContent | null;
  current: DgContent;
  /** The version's change notes, which a reconciled version's line is read from. */
  notes?: ArtifactChangeNotes | null | undefined;
}): string => {
  const before = new Set(previous === null ? [] : flattenQuestions(previous).map((q) => q.id));
  const after = new Set(flattenQuestions(current).map((question) => question.id));

  switch (reason) {
    case 'drafted_from_questions': {
      return 'Drafted from your research questions';
    }
    case 'redrafted_from_changed_questions': {
      return 'Redrafted from your changed research questions';
    }
    case 'redrafted_for_coverage': {
      return 'Redrafted to answer the research questions the check found unanswered';
    }
    case 'updated_for_changed_questions': {
      return 'Updated for your changed research questions';
    }
    case 'reworded_by_you': {
      return 'Your wording';
    }
    case 'added_at_your_request': {
      const added = flattenQuestions(current).filter((question) => !before.has(question.id));
      const polls = added.every((question) => question.type !== 'open_response');

      return polls && added.length > 0
        ? countLine(added.length, 'poll', 'polls', 'added at your request')
        : countLine(added.length, 'question', 'questions', 'added at your request');
    }
    case 'removed_at_your_request': {
      const removed = [...before].filter((id) => !after.has(id)).length;

      return countLine(removed, 'question', 'questions', 'removed at your request');
    }
    case 'reworded_at_your_request': {
      const was = new Map(
        previous === null ? [] : flattenQuestions(previous).map((q) => [q.id, q]),
      );
      const reworded = flattenQuestions(current).filter((question) => {
        const prior = was.get(question.id);

        return (
          prior !== undefined &&
          (prior.text !== question.text ||
            !sameList(answerWording(prior.options), answerWording(question.options)))
        );
      });

      return countLine(reworded.length, 'question', 'questions', 'reworded at your request');
    }
    case 'reordered_at_your_request': {
      return 'Reordered at your request';
    }
    case 'changed_at_your_request': {
      return 'Changed at your request';
    }
    case 'image_shown_at_your_request': {
      return countLine(
        stimulusChanges(previous, current).shown,
        'image',
        'images',
        'shown at your request',
      );
    }
    case 'image_removed_at_your_request': {
      return countLine(
        stimulusChanges(previous, current).removed,
        'image',
        'images',
        'removed at your request',
      );
    }
    case 'image_removed_from_materials': {
      return 'Image removed: it is no longer one of the images you marked to show';
    }
    case 'drafted_from_goal': {
      return 'Drafted from your goal';
    }
    case 'redrafted_from_changed_goal': {
      return 'Redrafted from your changed goal';
    }
    case 'reconciled': {
      return reconciledVersionText(notes);
    }
    // Audience reasons: the store never writes one on a guide.
    case 'drafted_from_guide':
    case 'updated_for_changed_guide':
    // Brief reasons: the store never writes one on a guide.
    case 'hidden_by_you':
    case 'shown_by_you':
    case 'reordered_by_you':
    case 'revised_by_sigora':
    case 'updated_from_follow_up':
    case null: {
      return '';
    }
  }
};

/**
 * What produced a whole guide an agent wrote: a first draft when there was no guide, a redraft from
 * the changed goal when the write claims a goal change, an update for the changed research questions
 * when it claims a change to them, and a change asked for otherwise.
 */
export const agentGuideWriteReason = ({
  hadGuide,
  claimed,
}: {
  hadGuide: boolean;
  claimed: readonly { upstream_kind: string }[];
}): ArtifactVersionReason => {
  if (!hadGuide) {
    return 'drafted_from_questions';
  }

  if (claimed.some((window) => window.upstream_kind === 'goal')) {
    return 'redrafted_from_changed_goal';
  }

  return claimed.length > 0 ? 'updated_for_changed_questions' : 'changed_at_your_request';
};

/** One option of a reworded poll: which option, and its new wording. */
export const dgOptionRewordSchema = z.strictObject({
  id: z.uuid(),
  text: z.string(),
});
export type DgOptionReword = z.infer<typeof dgOptionRewordSchema>;

/**
 * The researcher's rewording of one guide question: its text and, if given, the text of the named
 * options replaced, the question marked as their wording, and everything else — every other
 * question, and this one's type, options' ids, order and count, and what it serves — left as it was.
 *
 * Wording only, by construction: nothing here can add, remove, reorder or retype anything, so
 * structure cannot change through a wording edit. `options` must name exactly the stored options,
 * in their stored order. The same wording answers the content unchanged, which the store stores as
 * nothing.
 */
export const planDgReword = (request: {
  current: DgContent;
  questionId: string;
  text: string;
  options?: readonly DgOptionReword[] | undefined;
}): DgContent => rewordQuestion(request, 'user');

/**
 * One question's wording replaced, as `planDgReword` describes, and marked as `wordedBy`'s: the
 * researcher's on the card, Sigora's when the agent rewords it at their request.
 */
const rewordQuestion = (
  {
    current,
    questionId,
    text,
    options,
  }: {
    current: DgContent;
    questionId: string;
    text: string;
    options?: readonly DgOptionReword[] | undefined;
  },
  wordedBy: DgQuestion['worded_by'],
): DgContent => {
  const parsedText = questionWordsSchema.safeParse(text);
  if (!parsedText.success) {
    throw invalidGuide(
      `A guide question is at most ${String(MAX_DG_QUESTION_CHARS)} characters, and not empty.`,
      { issues: z.prettifyError(parsedText.error) },
    );
  }

  const optionTexts = (options ?? []).map((option) => optionWordsSchema.safeParse(option.text));
  const badOption = optionTexts.find((parsed) => !parsed.success);
  if (badOption !== undefined && !badOption.success) {
    throw invalidGuide(
      `A poll answer is at most ${String(MAX_DG_OPTION_CHARS)} characters, and not empty.`,
      { issues: z.prettifyError(badOption.error) },
    );
  }

  const target = flattenQuestions(current).find((question) => question.id === questionId);
  if (target === undefined) {
    throw questionNotFound(questionId);
  }

  const stored = target.options ?? [];
  if (options !== undefined) {
    const sameSet =
      options.length === stored.length &&
      options.every((option, index) => option.id === stored[index]?.id);
    if (!sameSet) {
      throw invalidGuide(
        'Only the wording of a poll’s answers can be edited here, not which answers it has or their order.',
        { question_id: questionId },
      );
    }
  }

  const nextOptions: DgOption[] = stored.map((option, index) => {
    const reworded = optionTexts[index];

    return reworded?.success === true ? { id: option.id, text: reworded.data } : option;
  });

  const unchanged =
    target.text === parsedText.data &&
    nextOptions.every((option, index) => option.text === stored[index]?.text);
  if (unchanged) {
    return current;
  }

  const reworded: DgQuestion = {
    ...target,
    text: parsedText.data,
    ...(target.options === undefined ? {} : { options: nextOptions }),
    worded_by: wordedBy,
  };

  return {
    sections: current.sections.map((section) => ({
      ...section,
      questions: section.questions.map((question) =>
        question.id === questionId ? reworded : question,
      ),
    })),
  };
};

/** A new question a reshape adds: everything but its ids and whose words it is in. */
export const dgQuestionDraftSchema = z.strictObject({
  type: z.enum(DG_QUESTION_TYPES),
  text: questionWordsSchema,
  options: z.array(optionWordsSchema).min(MIN_DG_OPTIONS).max(MAX_DG_OPTIONS_DRAFTED).optional(),
  serves_rq_ids: z.array(z.uuid()).min(1),
});
export type DgQuestionDraft = z.infer<typeof dgQuestionDraftSchema>;

/**
 * One change a reshape makes, applied in order:
 *
 * - `add` puts a new question into a section, after the named question or first when that is
 *   `null`; an added `single_choice` question is a poll;
 * - `remove` takes a question out, and a section it leaves empty goes with it;
 * - `move` puts a question after another, or first, in the same or another section, unchanged;
 * - `recast` changes a question's type and keeps its text: to an open question drops the options,
 *   to a poll needs them, and a poll's new options keep the id of any option whose text is the same;
 * - `reword` replaces a question's text and, if given, its options' text, as `planDgReword` does:
 *   the options named are exactly the stored ones in their order, and its id, type, what it serves
 *   and the image it shows are kept; the words are Sigora's;
 * - `show_image` shows one of the images the researcher marked to show just before a question, in
 *   place of any image it showed;
 * - `remove_image` stops a question showing an image, and changes nothing on one that shows none.
 */
export const dgReshapeOpSchema = z.discriminatedUnion('op', [
  z.strictObject({
    op: z.literal('add'),
    section_id: z.uuid(),
    after_question_id: z.uuid().nullable(),
    question: dgQuestionDraftSchema,
  }),
  z.strictObject({ op: z.literal('remove'), question_id: z.uuid() }),
  z.strictObject({
    op: z.literal('move'),
    question_id: z.uuid(),
    section_id: z.uuid(),
    after_question_id: z.uuid().nullable(),
  }),
  z.strictObject({
    op: z.literal('recast'),
    question_id: z.uuid(),
    type: z.enum(DG_QUESTION_TYPES),
    options: z.array(optionWordsSchema).min(MIN_DG_OPTIONS).max(MAX_DG_OPTIONS_DRAFTED).optional(),
  }),
  z.strictObject({
    op: z.literal('reword'),
    question_id: z.uuid(),
    text: questionWordsSchema,
    options: z
      .array(z.strictObject({ id: z.uuid(), text: optionWordsSchema }))
      .max(MAX_DG_OPTIONS_DRAFTED)
      .optional(),
  }),
  z.strictObject({ op: z.literal('show_image'), question_id: z.uuid(), file_id: z.uuid() }),
  z.strictObject({ op: z.literal('remove_image'), question_id: z.uuid() }),
]);
export type DgReshapeOp = z.infer<typeof dgReshapeOpSchema>;

/** What a reshape asks for: the changes, in order, and what the researcher asked, in a sentence. */
export const dgReshapeInputSchema = z.strictObject({
  ops: z.array(dgReshapeOpSchema).min(1).max(MAX_DG_RESHAPE_OPS),
  note: words(MAX_DG_NOTE_CHARS),
});
export type DgReshapeInput = z.infer<typeof dgReshapeInputSchema>;

const assertOptionsMatchType = (
  type: DgQuestion['type'],
  options: readonly string[] | undefined,
  questionId: string | null,
): void => {
  if (type === 'open_response' && options !== undefined) {
    throw invalidGuide('An open question has no answers to choose from.', {
      question_id: questionId,
    });
  }

  if (type !== 'open_response' && options === undefined) {
    throw invalidGuide('A poll needs the answers people choose from.', {
      question_id: questionId,
    });
  }
};

/** Where a question sits: its section and its index there. */
const locate = (
  sections: readonly DgSection[],
  questionId: string,
): { sectionIndex: number; questionIndex: number } | null => {
  for (const [sectionIndex, section] of sections.entries()) {
    const questionIndex = section.questions.findIndex((question) => question.id === questionId);
    if (questionIndex !== -1) {
      return { sectionIndex, questionIndex };
    }
  }

  return null;
};

/** The sections with one question inserted after another, or first; the anchor must be there. */
const insertInto = (
  sections: readonly DgSection[],
  sectionId: string,
  afterQuestionId: string | null,
  question: DgQuestion,
): DgSection[] => {
  const target = sections.find((section) => section.id === sectionId);
  if (target === undefined) {
    throw sectionNotFound(sectionId);
  }

  const at =
    afterQuestionId === null
      ? 0
      : target.questions.findIndex((existing) => existing.id === afterQuestionId) + 1;
  if (at === 0 && afterQuestionId !== null) {
    throw questionNotFound(afterQuestionId);
  }

  return sections.map((section) =>
    section.id === sectionId
      ? {
          ...section,
          questions: [...section.questions.slice(0, at), question, ...section.questions.slice(at)],
        }
      : section,
  );
};

/** The sections without one question; a section it leaves empty stays until `dropEmpty`. */
const withoutQuestion = (sections: readonly DgSection[], questionId: string): DgSection[] =>
  sections.map((section) => ({
    ...section,
    questions: section.questions.filter((question) => question.id !== questionId),
  }));

/** The sections that still hold a question: a section a change left empty goes with it. */
const dropEmpty = (sections: readonly DgSection[]): DgSection[] =>
  sections.filter((section) => section.questions.length > 0);

const reasonForOps = (ops: readonly DgReshapeOp[]): ArtifactVersionReason => {
  const kinds = new Set(ops.map((op) => op.op));
  if (kinds.size > 1) {
    return 'changed_at_your_request';
  }

  switch (ops[0]?.op) {
    case 'add': {
      return 'added_at_your_request';
    }
    case 'remove': {
      return 'removed_at_your_request';
    }
    case 'move': {
      return 'reordered_at_your_request';
    }
    case 'reword': {
      return 'reworded_at_your_request';
    }
    case 'show_image': {
      return 'image_shown_at_your_request';
    }
    case 'remove_image': {
      return 'image_removed_at_your_request';
    }
    default: {
      return 'changed_at_your_request';
    }
  }
};

const imageNotCandidate = (fileId: string): DomainError =>
  new DomainError(
    'dg_image_not_candidate',
    'That is not one of the images the researcher marked to show participants on the Materials step. Only those can be shown.',
    { file_id: fileId },
  );

/** The sections with one question replaced by what `change` makes of it. */
const replaceQuestion = (
  sections: readonly DgSection[],
  questionId: string,
  change: (question: DgQuestion) => DgQuestion,
): DgSection[] =>
  sections.map((section) =>
    section.questions.some((question) => question.id === questionId)
      ? {
          ...section,
          questions: section.questions.map((question) =>
            question.id === questionId ? change(question) : question,
          ),
        }
      : section,
  );

/** A question as it is, without the image it shows. */
const withoutStimulus = ({ stimulus: _stimulus, ...question }: DgQuestion): DgQuestion => question;

/**
 * The stored guide with the requested changes applied in order, and the reason that version is
 * written for.
 *
 * Every question no change names is carried over exactly as it is stored — id, wording, type,
 * options, what it serves and whose words it is in — and a moved question is carried whole, so a
 * reshape cannot reword or re-id anything by accident. New questions and options get ids from
 * `mintId`, never from the request, and are in Sigora's words, as is a question a `reword` names:
 * the agent wrote those words, so a later reconciliation or repair does not keep them as the
 * researcher's.
 *
 * An image is shown through `resolveImage`, which answers the stimulus a file is shown as — pinned to
 * the Materials version it is chosen from — or `null` for a file that is not one of the images the
 * researcher marked to show. A moved or recast question keeps the image it shows, and a removed one
 * takes it with it.
 *
 * Refused, in order: `dg_question_not_found` / `dg_section_not_found` and `invalid_content` for a
 * change that does not fit the guide, `dg_image_not_candidate` and `dg_image_already_shown` for an
 * image that cannot be shown there (each as the change is applied), `dg_serves_unknown_rq` for an
 * added question serving a research question the study does not have, `dg_question_kind_unavailable`,
 * then `dg_rq_unserved` when the result leaves a current research question with nothing serving it
 * that the stored guide served.
 * A guide is never refused for its length: that is weighed by the agent, not by the store.
 */
export const planDgReshape = ({
  current,
  ops,
  rqIds,
  mintId,
  resolveImage,
}: {
  current: DgContent;
  ops: readonly DgReshapeOp[];
  /** The ids of the study's current research questions. */
  rqIds: readonly string[];
  mintId: () => string;
  /** The stimulus a file is shown as, or `null` for one that is not an image to show. */
  resolveImage: (fileId: string) => DgStimulus | null;
}): { content: DgContent; reason: ArtifactVersionReason } => {
  const known = new Set(rqIds);
  let sections: DgSection[] = current.sections.map((section) => ({ ...section }));

  for (const op of ops) {
    switch (op.op) {
      case 'add': {
        assertOptionsMatchType(op.question.type, op.question.options, null);
        const unknown = op.question.serves_rq_ids.filter((id) => !known.has(id));
        if (unknown.length > 0) {
          throw new DomainError(
            'dg_serves_unknown_rq',
            'That question serves a research question this study does not have.',
            { unknown_rq_ids: unknown },
          );
        }

        const question: DgQuestion = {
          id: mintId(),
          type: op.question.type,
          text: op.question.text,
          ...(op.question.options === undefined
            ? {}
            : { options: op.question.options.map((text) => ({ id: mintId(), text })) }),
          serves_rq_ids: [...new Set(op.question.serves_rq_ids)],
          worded_by: 'sigora',
        };
        sections = insertInto(sections, op.section_id, op.after_question_id, question);
        break;
      }
      case 'remove': {
        if (locate(sections, op.question_id) === null) {
          throw questionNotFound(op.question_id);
        }

        sections = dropEmpty(withoutQuestion(sections, op.question_id));
        break;
      }
      case 'move': {
        const at = locate(sections, op.question_id);
        if (at === null) {
          throw questionNotFound(op.question_id);
        }

        if (op.after_question_id === op.question_id) {
          throw invalidGuide('A question cannot be moved after itself.', {
            question_id: op.question_id,
          });
        }

        const moving = sections[at.sectionIndex]?.questions[at.questionIndex] as DgQuestion;
        sections = dropEmpty(
          insertInto(
            withoutQuestion(sections, op.question_id),
            op.section_id,
            op.after_question_id,
            moving,
          ),
        );
        break;
      }
      case 'recast': {
        const at = locate(sections, op.question_id);
        if (at === null) {
          throw questionNotFound(op.question_id);
        }

        const question = sections[at.sectionIndex]?.questions[at.questionIndex] as DgQuestion;
        const staysPoll = question.type !== 'open_response' && op.type !== 'open_response';
        const texts =
          op.options ?? (staysPoll ? question.options?.map((option) => option.text) : undefined);
        assertOptionsMatchType(op.type, texts, question.id);

        const keep = new Map((question.options ?? []).map((option) => [option.text, option.id]));
        const recast: DgQuestion = {
          id: question.id,
          type: op.type,
          text: question.text,
          ...(texts === undefined
            ? {}
            : { options: texts.map((text) => ({ id: keep.get(text) ?? mintId(), text })) }),
          serves_rq_ids: question.serves_rq_ids,
          // One badge covers the question and its answers, so answers Sigora wrote over the
          // researcher's make the whole question Sigora's again. Dropping the answers, or keeping
          // them word for word, leaves the researcher's wording as it was.
          worded_by:
            texts === undefined || sameList(texts, answerWording(question.options))
              ? question.worded_by
              : 'sigora',
          ...(question.stimulus === undefined ? {} : { stimulus: question.stimulus }),
        };
        sections = sections.map((section, index) =>
          index === at.sectionIndex
            ? {
                ...section,
                questions: section.questions.map((existing) =>
                  existing.id === question.id ? recast : existing,
                ),
              }
            : section,
        );
        break;
      }
      case 'reword': {
        sections = rewordQuestion(
          {
            current: { sections },
            questionId: op.question_id,
            text: op.text,
            options: op.options,
          },
          'sigora',
        ).sections;
        break;
      }
      case 'show_image': {
        if (locate(sections, op.question_id) === null) {
          throw questionNotFound(op.question_id);
        }

        const stimulus = resolveImage(op.file_id);
        if (stimulus === null) {
          throw imageNotCandidate(op.file_id);
        }

        const shownOn = flattenQuestions({ sections }).find(
          (question) => question.stimulus?.file_id === op.file_id,
        );
        if (shownOn !== undefined && shownOn.id !== op.question_id) {
          throw new DomainError(
            'dg_image_already_shown',
            'That image is already shown with another question, and an image is shown once. To show it here instead, remove it from that question in the same change.',
            { file_id: op.file_id, question_id: shownOn.id },
          );
        }

        // Shown already: the pin it was shown with stands, so asking again changes nothing.
        if (shownOn === undefined) {
          sections = replaceQuestion(sections, op.question_id, (question) => ({
            ...question,
            stimulus,
          }));
        }
        break;
      }
      case 'remove_image': {
        if (locate(sections, op.question_id) === null) {
          throw questionNotFound(op.question_id);
        }

        sections = replaceQuestion(sections, op.question_id, withoutStimulus);
        break;
      }
    }
  }

  const content: DgContent = { sections };
  assertGeneratedDgKinds(content);
  // Only a research question this reshape leaves unserved is refused. One the guide already left
  // unserved is the guide's own state rather than this change's, and must not stop every other
  // change, a reorder included.
  const alreadyUnserved = new Set(unservedRqIds(current, rqIds));
  assertEveryRqServed(
    content,
    rqIds.filter((id) => !alreadyUnserved.has(id)),
  );

  if (sections.length === 0) {
    throw invalidGuide('That would remove every question; a guide keeps at least one.');
  }

  return { content, reason: reasonForOps(ops) };
};

/**
 * A whole guide proposed from outside the store — the guide reconciler's answer, once
 * its handles are mapped to ids: the stored shape without whose words each question is in, and
 * lenient about anything else it adds, which is ignored, an image included. Only ever converted with
 * `toDgContent`, which decides the rest on the server.
 */
export const dgProposalSchema = z.object({
  sections: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      questions: z.array(
        z.object({
          id: z.uuid(),
          type: z.enum(DG_QUESTION_TYPES),
          text: z.string(),
          options: z.array(z.object({ id: z.uuid(), text: z.string() })).optional(),
          serves_rq_ids: z.array(z.uuid()),
        }),
      ),
    }),
  ),
});
export type DgProposal = z.infer<typeof dgProposalSchema>;

/**
 * Parses a model's guide proposal, refusing one that is not a guide as `invalid_content` so the
 * model reads what was wrong with it.
 */
export const parseDgProposal = (content: unknown): DgProposal => {
  const parsed = dgProposalSchema.safeParse(content);
  if (!parsed.success) {
    throw invalidGuide(`That is not a guide: ${z.prettifyError(parsed.error)}`, {
      issues: z.prettifyError(parsed.error),
    });
  }

  return parsed.data;
};

/**
 * A proposed guide as the store writes it: whose words each question is in, decided here.
 *
 * A question that exists in the current guide with the same id, text and option texts keeps that
 * question's `worded_by`, so a whole-document rewrite leaves the researcher's wording marked as
 * theirs; anything else is Sigora's. A question that exists in the current guide keeps the image it
 * shows there, so a proposal can neither add, move nor remove an image, and a question it drops
 * takes its image with it. The result still goes through the strict content parse and the
 * generated-kinds rule at the store.
 */
export const toDgContent = (proposal: DgProposal, current: DgContent | null): DgContent => {
  const stored = new Map(
    (current === null ? [] : flattenQuestions(current)).map((question) => [question.id, question]),
  );

  return {
    sections: proposal.sections.map((section) => ({
      id: section.id,
      title: section.title,
      questions: section.questions.map((question) => {
        const before = stored.get(question.id);
        const kept =
          before !== undefined &&
          before.text === question.text &&
          sameList(answerWording(before.options), answerWording(question.options));

        return {
          id: question.id,
          type: question.type,
          text: question.text,
          ...(question.options === undefined
            ? {}
            : {
                options: question.options.map((option) => ({ id: option.id, text: option.text })),
              }),
          serves_rq_ids: question.serves_rq_ids,
          worded_by: kept ? before.worded_by : 'sigora',
          ...(before?.stimulus === undefined ? {} : { stimulus: before.stimulus }),
        };
      }),
    })),
  };
};

/**
 * What a guide's images are checked against: the study's Materials artifact, the images its current
 * version marks to show, and the images each version a stimulus pins marks to show. A version the
 * study's Materials do not have is absent from `candidatesByVersion`.
 */
export interface StimulusMaterials {
  artifactId: string | null;
  currentCandidates: readonly string[];
  candidatesByVersion: ReadonlyMap<number, readonly string[]>;
}

/**
 * The questions whose image no longer resolves: one that names another artifact than the study's
 * Materials, pins a version those Materials do not have or that did not mark it to show, or is not
 * one the current Materials mark to show. A file is the stored row, so a pin that is not the current
 * version still resolves while both versions mark the same file.
 */
export const unresolvedStimulusQuestionIds = (
  content: DgContent,
  materials: StimulusMaterials,
): string[] =>
  flattenQuestions(content)
    .filter(({ stimulus }) => {
      if (stimulus === undefined) {
        return false;
      }

      return (
        stimulus.materials_artifact_id !== materials.artifactId ||
        !(materials.candidatesByVersion.get(stimulus.materials_version) ?? []).includes(
          stimulus.file_id,
        ) ||
        !materials.currentCandidates.includes(stimulus.file_id)
      );
    })
    .map((question) => question.id);

/**
 * Why a guide's images cannot stand, or `null` when every one resolves (`unresolvedStimulusQuestionIds`)
 * and none is shown twice: `dg_image_already_shown` or `dg_image_not_current`.
 *
 * `stored` is the guide a write transforms when the write can change no image — the researcher's
 * rewording — so an image a question already showed there, unchanged, is not this write's to answer
 * for. Every other caller passes `null`.
 */
export const stimulusRefusal = (
  content: DgContent,
  materials: StimulusMaterials,
  stored: DgContent | null = null,
): DomainError | null => {
  const shown = new Map<string, string>();
  for (const question of flattenQuestions(content)) {
    const fileId = question.stimulus?.file_id;
    if (fileId === undefined) {
      continue;
    }

    const first = shown.get(fileId);
    if (first !== undefined) {
      return new DomainError(
        'dg_image_already_shown',
        'That image is already shown with another question, and an image is shown once.',
        { file_id: fileId, question_id: first },
      );
    }
    shown.set(fileId, question.id);
  }

  const before = new Map(
    (stored === null ? [] : flattenQuestions(stored)).map((q) => [q.id, q.stimulus] as const),
  );
  const unresolved = unresolvedStimulusQuestionIds(content, materials).filter((questionId) => {
    const question = flattenQuestions(content).find((candidate) => candidate.id === questionId);

    return !sameStimulus(question?.stimulus, before.get(questionId));
  });
  if (unresolved.length === 0) {
    return null;
  }

  return new DomainError(
    'dg_image_not_current',
    'This guide shows an image that is no longer one of the images the researcher marked to show on the Materials step. Remove it from the guide, or show another one.',
    { question_ids: unresolved },
  );
};

/**
 * The guide without the images that no longer resolve against `materials`, every other question as
 * it is stored, or `null` when every image still resolves. What a Materials version that stops
 * marking an image to show does to the guide showing it.
 */
export const withoutUnresolvedStimuli = (
  content: DgContent,
  materials: StimulusMaterials,
): DgContent | null => {
  const unresolved = new Set(unresolvedStimulusQuestionIds(content, materials));
  if (unresolved.size === 0) {
    return null;
  }

  return {
    sections: content.sections.map((section) =>
      section.questions.some((question) => unresolved.has(question.id))
        ? {
            ...section,
            questions: section.questions.map((question) =>
              unresolved.has(question.id) ? withoutStimulus(question) : question,
            ),
          }
        : section,
    ),
  };
};

/** The stimuli a Materials version's marked images are shown as, pinned to that version, in order. */
export const pinnedStimuli = ({
  artifactId,
  version,
  candidates,
}: {
  artifactId: string;
  version: number;
  candidates: readonly string[];
}): DgStimulus[] =>
  candidates.map((fileId) => ({
    materials_artifact_id: artifactId,
    materials_version: version,
    file_id: fileId,
  }));
